import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import Layout from '../components/Layout';
import { useAuth } from '../utils/authContext';
import { Truck, Plus, Edit2, Trash2, MapPin, X } from 'lucide-react';
import { formatPrice } from '../utils/formatPrice';

const GET_SHIPPING_OPTIONS = gql`
  query GetShippingOptions($catalogId: ID!) {
    getShippingOptions(catalogId: $catalogId) {
      id
      name
      details
      estimatedDuration
      price
      isPickup
    }
  }
`;

const SAVE_SHIPPING_OPTION = gql`
  mutation SaveShippingOption($input: ShippingOptionInput!) {
    saveShippingOption(input: $input) {
      id
      name
      details
      estimatedDuration
      price
      isPickup
    }
  }
`;

const DELETE_SHIPPING_OPTION = gql`
  mutation DeleteShippingOption($id: ID!, $catalogId: ID!) {
    deleteShippingOption(id: $id, catalogId: $catalogId)
  }
`;

const ShippingOptions = () => {
    const { selectedCatalog } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingOption, setEditingOption] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        details: '',
        estimatedDuration: '',
        price: '',
        isPickup: false
    });

    const { data, loading, refetch } = useQuery(GET_SHIPPING_OPTIONS, {
        variables: { catalogId: selectedCatalog?.id },
        skip: !selectedCatalog?.id
    });

    const [saveShippingOption] = useMutation(SAVE_SHIPPING_OPTION);
    const [deleteShippingOption] = useMutation(DELETE_SHIPPING_OPTION);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const input = {
                ...formData,
                catalogId: selectedCatalog.id,
                id: editingOption?.id,
                price: formData.isPickup ? 0 : parseFloat(formData.price),
                name: formData.isPickup ? 'Pickup' : formData.name
            };

            // Remove __typename if it exists
            if (input.__typename) {
                delete input.__typename;
            }

            await saveShippingOption({
                variables: { input }
            });
            refetch();
            setIsModalOpen(false);
            resetForm();
        } catch (error) {
            console.error('Error saving shipping option:', error);
        }
    };

    const handleEdit = (option) => {
        setEditingOption(option);
        setFormData(option);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this shipping option?')) {
            try {
                await deleteShippingOption({
                    variables: {
                        id,
                        catalogId: selectedCatalog.id
                    }
                });
                refetch();
            } catch (error) {
                console.error('Error deleting shipping option:', error);
            }
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            details: '',
            estimatedDuration: '',
            price: '',
            isPickup: false
        });
        setEditingOption(null);
    };
    const sortedOptions = useMemo(() => {
        if (!data?.getShippingOptions) return [];
        return data.getShippingOptions.slice().sort((a, b) => {
            if (a.isPickup && !b.isPickup) return -1;
            if (!a.isPickup && b.isPickup) return 1;
            return 0;
        });
    }, [data?.getShippingOptions]);

    if (loading) return (
        <div className="p-4">
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-6"></div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((n) => (
                    <div key={n} className="bg-white p-4 rounded-lg shadow animate-pulse">
                        <div className="h-6 w-3/4 bg-gray-200 rounded mb-3"></div>
                        <div className="h-4 w-full bg-gray-200 rounded mb-3"></div>
                        <div className="h-6 w-1/3 bg-gray-200 rounded"></div>
                    </div>
                ))}
            </div>
        </div>
    );

    const hasShippingOptions = data?.getShippingOptions?.length > 0;

    return (
        <Layout title="Shipping Options" description="Manage your shipping options">
            <div className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center mb-6 gap-4">
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className={`${!hasShippingOptions ? 'w-full sm:w-auto animate-bounce' : 'w-full sm:w-auto'} 
                            bg-[#191960] text-white px-4 py-2.5 rounded-lg flex items-center justify-center 
                            hover:bg-[#13134d] transition-colors duration-200`}
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Add Shipping Option
                    </button>
                </div>

                {!hasShippingOptions ? (
                    <div className="text-center py-12 px-4">
                        <div className="flex justify-center mb-6">
                            <div className="relative">
                                <div className="absolute inset-0 bg-[#191960] opacity-10 rounded-full animate-ping"></div>
                                <div className="relative bg-[#191960] p-4 rounded-full">
                                    <Truck className="w-12 h-12 text-white" />
                                </div>
                            </div>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            No shipping options yet
                        </h3>
                        <p className="text-gray-600 mb-8 max-w-md mx-auto">
                            Add shipping options to let your customers know how they can receive their orders.
                            You can set up delivery zones or pickup locations.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto">
                            <div className="bg-gray-50 p-4 rounded-lg flex items-center gap-3 w-full">
                                <div className="bg-blue-100 p-2 rounded-full">
                                    <Truck className="w-5 h-5 text-blue-600" />
                                </div>
                                <div className="text-left">
                                    <h4 className="font-medium text-gray-900">Delivery Zone</h4>
                                    <p className="text-sm text-gray-600">Set up areas you deliver to</p>
                                </div>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg flex items-center gap-3 w-full">
                                <div className="bg-green-100 p-2 rounded-full">
                                    <MapPin className="w-5 h-5 text-green-600" />
                                </div>
                                <div className="text-left">
                                    <h4 className="font-medium text-gray-900">Pickup Location</h4>
                                    <p className="text-sm text-gray-600">Add store pickup options</p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {sortedOptions.map((option) => (
                            <div
                                key={option.id}
                                className="bg-white p-5 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100"
                            >
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-center">
                                        {option.isPickup ? (
                                            <div className="bg-green-100 p-2 rounded-full">
                                                <MapPin className="w-5 h-5 text-green-600" />
                                            </div>
                                        ) : (
                                            <div className="bg-blue-100 p-2 rounded-full">
                                                <Truck className="w-5 h-5 text-blue-600" />
                                            </div>
                                        )}
                                        <h3 className="font-semibold ml-3">{option.name}</h3>
                                    </div>
                                    <div className="flex space-x-2 ml-4">
                                        <button
                                            onClick={() => handleEdit(option)}
                                            className="p-1.5 rounded-full hover:bg-gray-100 bg-gray-50 text-gray-600 transition-colors duration-200"
                                            aria-label="Edit"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(option.id)}
                                            className="p-1.5 rounded-full hover:bg-red-100 bg-gray-50 text-gray-600 hover:text-red-600 transition-colors duration-200"
                                            aria-label="Delete"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">{option.details}</p>
                                <div className="mt-4 flex justify-between items-center pt-3 border-t border-gray-100">
                                    <span className="font-medium text-lg text-gray-900">
                                        {option.isPickup ? 'Free' : formatPrice(option.price)}
                                    </span>
                                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                        {option.estimatedDuration}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center mb-4">
                                <h4 className="text-xl font-bold text-[#191970]">
                                    {editingOption ? 'Edit Shipping Option' : 'Add Shipping Option'}
                                </h4>
                                <button
                                    onClick={() => { setIsModalOpen(false); resetForm(); }}
                                    className="text-gray-500 bg-gray-100 hover:text-gray-700"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="flex items-center mb-4">
                                    <input
                                        type="checkbox"
                                        name="isPickup"
                                        checked={formData.isPickup}
                                        onChange={handleInputChange}
                                        className="mr-2 bg-white"
                                    />
                                    <label className="text-sm font-medium text-gray-500">This is a pickup location</label>
                                </div>

                                {!formData.isPickup && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">
                                            Region Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required={!formData.isPickup}
                                            placeholder="e.g. Lagos Mainland"
                                            className="mt-1 px-2 py-2 block w-full bg-white rounded-md border-gray-300 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50 h-10"
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-500">
                                        {formData.isPickup ? 'Pickup Address & Hours *' : 'Coverage Area *'}
                                    </label>
                                    <textarea
                                        name="details"
                                        value={formData.details}
                                        onChange={handleInputChange}
                                        required
                                        placeholder={formData.isPickup ?
                                            "e.g. 123 Shop Street, Ikeja (Mon-Fri: 9AM-5PM)" :
                                            "e.g. Covers Ikeja, Oshodi, Surulere"
                                        }
                                        className="mt-1 px-2 py-2 block w-full bg-white rounded-md border-gray-300 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50 h-20"
                                        rows="3"
                                    />
                                </div>

                                {!formData.isPickup && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500">
                                                Price * (₦)
                                            </label>
                                            <div className="flex flex-row">
                                                <p className="mt-4 mr-2">₦</p>
                                                <input
                                                    type="number"
                                                    name="price"
                                                    value={formData.price}
                                                    onChange={handleInputChange}
                                                    required
                                                    min="0"
                                                    className="mt-1 px-2 py-2 block w-full bg-white rounded-md border-gray-300 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50 h-10"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500">
                                                Estimated Duration *
                                            </label>
                                            <input
                                                type="text"
                                                name="estimatedDuration"
                                                value={formData.estimatedDuration}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="e.g. 2-3 days"
                                                className="mt-1 px-2 py-2 block w-full bg-white rounded-md border-gray-300 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50 h-10"
                                            />
                                        </div>
                                    </>
                                )}

                                <div className="flex justify-end space-x-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsModalOpen(false);
                                            resetForm();
                                        }}
                                        className="px-4 py-2 text-gray-600 bg-gray-50 hover:text-gray-800"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-[#191970] text-white rounded-lg hover:bg-opacity-90"
                                    >
                                        {editingOption ? 'Save Changes' : 'Add Option'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default ShippingOptions;
